import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import Table from "../ogComponent/table";
import AddDataModal from "./addDatamodal";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { IconArrowBackUp, IconPlus } from "@tabler/icons-react";
import Spinner from "../ogComponent/spinner";

export default function StudentDetails() {
  const { id } = useParams();
  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedEnrollmentId, setSelectedEnrollmentId] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const navigate = useNavigate();

  // Access permissions array from Redux store
  const permissions = useSelector((state) => state.emp.permissions);

  const pagePermissions = ["editstudentsID", "showfeeID", "showfeerecieptID"];

  // Handle page change
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  // Fetch student details and table data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const accessToken = localStorage.getItem("accessToken");
        if (!accessToken) {
          throw new Error("No access token found");
        }

        // Fetch student details
        const studentResponse = await axios.get(
          `${process.env.REACT_APP_API}/api/course/students/${id}`,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
              "Content-Type": "application/json",
            },
          }
        );
        setStudent(studentResponse.data);

        // Fetch enrollments only if showfeeID permission is present
        if (permissions.includes("showfeeID")) {
          const enrollmentsResponse = await axios.get(
            `${process.env.REACT_APP_API}/api/course/enrollments?student_id=${id}`,
            {
              headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json",
              },
            }
          );

          // Map enrollments to table structure
          const mappedData = enrollmentsResponse.data.map((item, index) => ({
            count: index + 1,
            id: item.id,
            date: item.enrollment_date || "N/A",
            method: item.payment_method,
            amount: item.amount_paid,
            edit: permissions.includes("editstudentsID") ? (
              <svg
                onClick={(e) => {
                  e.stopPropagation();
                }}
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="icon icon-tabler icons-tabler-outline icon-tabler-edit"
              >
                <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                <path d="M7 7h-1a2 2 0 0 0 -2 2v9a2 2 0 0 0 2 2h9a2 2 0 0 0 2 -2v-1" />
                <path d="M20.385 6.585a2.1 2.1 0 0 0 -2.97 -2.97l-8.415 8.385v3h3l8.385 -8.415z" />
                <path d="M16 5l3 3" />
              </svg>
            ) : null,
            delete: permissions.includes("editstudentsID") ? (
              <svg
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedEnrollmentId(item.id);
                  setIsDeleteModalOpen(true);
                }}
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="icon icon-tabler icons-tabler-outline icon-tabler-trash"
              >
                <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                <path d="M4 7l16 0" />
                <path d="M10 11l0 6" />
                <path d="M14 11l0 6" />
                <path d="M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2 -2l1 -12" />
                <path d="M9 7v-3a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v3" />
              </svg>
            ) : null,
          }));

          setTableData(mappedData);
        }
      } catch (err) {
        setError(err.message);
        toast.error(err.message || "Failed to fetch student data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, permissions]);

  // Handle form submission for adding new data
  const handleAddData = async (formData) => {
    if (!permissions.includes("editstudentsID")) return;

    try {
      const accessToken = localStorage.getItem("accessToken");
      if (!accessToken) {
        throw new Error("No access token found");
      }

      const payload = {
        student_id: parseInt(id),
        course_id: student.selected_course[0]?.id || 1,
        payment_method: formData.payment_method,
        amount_paid: parseFloat(formData.amount_paid),
        enrollment_date: formData.enrollment_date,
        description: formData.description,
      };

      const response = await axios.post(
        `${process.env.REACT_APP_API}/api/course/enrollments/`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
        }
      );

      // Refetch table data only if showfeeID permission is present
      if (permissions.includes("showfeeID")) {
        const enrollmentsResponse = await axios.get(
          `${process.env.REACT_APP_API}/api/course/enrollments?student_id=${id}`,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
              "Content-Type": "application/json",
            },
          }
        );

        const mappedData = enrollmentsResponse.data.map((item, index) => ({
          count: index + 1,
          id: item.id,
          date: item.enrollment_date || "N/A",
          method: item.payment_method,
          amount: item.amount_paid,
          edit: permissions.includes("editstudentsID") ? (
            <svg
              onClick={(e) => {
                e.stopPropagation();
              }}
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="icon icon-tabler icons-tabler-outline icon-tabler-edit"
            >
              <path stroke="none" d="M0 0h24v24H0z" fill="none" />
              <path d="M7 7h-1a2 2 0 0 0 -2 2v9a2 2 0 0 0 2 2h9a2 2 0 0 0 2 -2v-1" />
              <path d="M20.385 6.585a2.1 2.1 0 0 0 -2.97 -2.97l-8.415 8.385v3h3l8.385 -8.415z" />
              <path d="M16 5l3 3" />
            </svg>
          ) : null,
          delete: permissions.includes("editstudentsID") ? (
            <svg
              onClick={(e) => {
                e.stopPropagation();
                setSelectedEnrollmentId(item.id);
                setIsDeleteModalOpen(true);
              }}
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="icon icon-tabler icons-tabler-outline icon-tabler-trash"
            >
              <path stroke="none" d="M0 0h24v24H0z" fill="none" />
              <path d="M4 7l16 0" />
              <path d="M10 11l0 6" />
              <path d="M14 11l0 6" />
              <path d="M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2 -2l1 -12" />
              <path d="M9 7v-3a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v3" />
            </svg>
          ) : null,
        }));

        setTableData(mappedData);
      }
      toast.success("Enrollment added successfully!");
    } catch (err) {
      console.error("Error submitting data:", err);
      toast.error("Failed to add enrollment.");
    }
  };

  // Handle delete enrollment
  const handleDeleteEnrollment = async () => {
    if (!permissions.includes("editstudentsID")) return;

    try {
      const accessToken = localStorage.getItem("accessToken");
      if (!accessToken) {
        throw new Error("No access token found");
      }

      await axios.delete(
        `${process.env.REACT_APP_API}/api/course/enrollments/${selectedEnrollmentId}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      toast.success("Enrollment deleted successfully!");

      // Refetch table data only if showfeeID permission is present
      if (permissions.includes("showfeeID")) {
        const enrollmentsResponse = await axios.get(
          `${process.env.REACT_APP_API}/api/course/enrollments?student_id=${id}`,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
              "Content-Type": "application/json",
            },
          }
        );

        const mappedData = enrollmentsResponse.data.map((item, index) => ({
          count: index + 1,
          id: item.id,
          date: item.enrollment_date || "N/A",
          method: item.payment_method,
          amount: item.amount_paid,
          edit: permissions.includes("editstudentsID") ? (
            <svg
              onClick={(e) => {
                e.stopPropagation();
              }}
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="icon icon-tabler icons-tabler-outline icon-tabler-edit"
            >
              <path stroke="none" d="M0 0h24v24H0z" fill="none" />
              <path d="M7 7h-1a2 2 0 0 0 -2 2v9a2 2 0 0 0 2 2h9a2 2 0 0 0 2 -2v-1" />
              <path d="M20.385 6.585a2.1 2.1 0 0 0 -2.97 -2.97l-8.415 8.385v3h3l8.385 -8.415z" />
              <path d="M16 5l3 3" />
            </svg>
          ) : null,
          delete: permissions.includes("editstudentsID") ? (
            <svg
              onClick={(e) => {
                e.stopPropagation();
                setSelectedEnrollmentId(item.id);
                setIsDeleteModalOpen(true);
              }}
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="icon icon-tabler icons-tabler-outline icon-tabler-trash"
            >
              <path stroke="none" d="M0 0h24v24H0z" fill="none" />
              <path d="M4 7l16 0" />
              <path d="M10 11l0 6" />
              <path d="M14 11l0 6" />
              <path d="M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2 -2l1 -12" />
              <path d="M9 7v-3a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v3" />
            </svg>
          ) : null,
        }));

        setTableData(mappedData);
      }
    } catch (err) {
      console.error("Error deleting enrollment:", err);
      toast.error("Failed to delete enrollment.");
    } finally {
      setIsDeleteModalOpen(false);
    }
  };

  const handleChange = (item) => {
    if (permissions.includes("showfeerecieptID")) {
      navigate(`/user/bill/${item.id}`);
    }
  };

  if (loading) {
    return <Spinner />;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  if (!student) {
    return <div>No student data found</div>;
  }

  return (
    <div className="container py-5 px-5 position-relative">
      <div
        className="position-absolute px-3 bg-secondary text-white py-1 rounded-2"
        style={{ top: "30px", left: "30px", zIndex: "2", cursor: "pointer" }}
        onClick={() => navigate(-1)}
      >
        Back <IconArrowBackUp stroke={2} />
      </div>

      <div className="card shadow-lg p-4 rounded-4">
        <div className="row">
          <div className="col-md-4 text-center">
            <img
              src={student.profile_photo || "/assets/user.png"}
              alt="Profile"
              className="img-fluid rounded-circle mb-3"
              style={{ width: "200px", height: "200px", objectFit: "cover" }}
            />
            <h5 className="fw-bold">{student.full_name}</h5>
          </div>

          <div className="col-md-8">
            <h4 className="mb-4">Student Details</h4>
            <div className="row">
              {[
                { label: "Email", value: student.email },
                { label: "Contact No", value: student.contact_number },
                { label: "WhatsApp No", value: student.whatsapp_number },
                { label: "Aadhar No", value: student.aadhar_number },
                { label: "School", value: student.school },
                { label: "Standard", value: student.standard },
                { label: "Medium", value: student.medium },
                {
                  label: "Location",
                  value: `${student.location_city}, ${student.location_state}, ${student.location_country}`,
                },
                { label: "Fee Amount", value: student.fee_amount || "N/A" },
                { label: "Amount Paid", value: student.amount_paid || "N/A" },
                {
                  label: "Courses",
                  value: student.selected_course.length ? (
                    <ul>
                      {student.selected_course.map((course, index) => (
                        <li key={index}>{course.course_name}</li>
                      ))}
                    </ul>
                  ) : (
                    "N/A"
                  ),
                },
                {
                  label: "Batches",
                  value: student.selected_batch.length ? (
                    <ul>
                      {student.selected_batch.map((batch, index) => (
                        <li key={index}>{batch.name}</li>
                      ))}
                    </ul>
                  ) : (
                    "N/A"
                  ),
                },
              ].map((item, index) => (
                <div className="col-md-6 mb-2" key={index}>
                  <strong>{item.label}:</strong>{" "}
                  {typeof item.value === "string" || typeof item.value === "number" ? item.value : item.value}
                </div>
              ))}
            </div>
          </div>
        </div>

        <hr />
        <h4 className="mb-4">Parent Details</h4>
        <div className="row">
          {[
            { label: "Parent Name", value: student.parent_name },
            { label: "Relation", value: student.parent_relation },
            { label: "Phone Number", value: student.parent_phone_no },
            { label: "WhatsApp Number", value: student.parent_whatsapp_no },
          ].map((item, index) => (
            <div className="col-md-6 mb-2" key={index}>
              <strong>{item.label}:</strong> {item.value || "N/A"}
            </div>
          ))}
        </div>
      </div>

      <div className="mt-5">
        {permissions.includes("editstudentsID") && (
          <div className="w-100 d-flex justify-content-end my-3">
            <button
              className="btn btn-success"
              onClick={() => setIsModalOpen(true)}
            >
              Add New Fee <IconPlus stroke={2} size={20} />
            </button>
          </div>
        )}

        {permissions.includes("showfeeID") ? (
          tableData.length > 0 ? (
            <Table
              header={[
                { name: "count", label: "#", active: true },
                { name: "date", label: "Date", active: true },
                { name: "method", label: "Payment Method", active: true },
                { name: "amount", label: "Amount", active: true },
                { name: "edit", label: "", active: permissions.includes("editstudentsID") },
                { name: "delete", label: "", active: permissions.includes("editstudentsID") },
              ]}
              product={tableData}
              onRowClick={handleChange}
              currentPage={currentPage}
              onPageChange={handlePageChange}
            />
          ) : (
            <div className="w-100 d-flex justify-content-center fs-4">
              No Fee Data's!
            </div>
          )
        ) : (
          <p>No permission to view fee data.</p>
        )}
      </div>

      <AddDataModal
        isOpen={isModalOpen && permissions.includes("editstudentsID")}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleAddData}
        studentId={id}
        courseId={student.selected_course[0]?.id || 1}
      />

      {isDeleteModalOpen && permissions.includes("editstudentsID") && (
        <div
          className={`modal fade ${isDeleteModalOpen ? "show" : ""}`}
          id="deleteModal"
          tabIndex="-1"
          aria-labelledby="deleteModalLabel"
          aria-hidden="true"
          style={{ display: isDeleteModalOpen ? "block" : "none" }}
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" id="deleteModalLabel">
                  Confirm Delete
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setIsDeleteModalOpen(false)}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body">
                Are you sure you want to delete this enrollment?
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setIsDeleteModalOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleDeleteEnrollment}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ToastContainer />
    </div>
  );
}